import React, {useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Switch, Text, TouchableOpacity, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import {
  accentColor,
  switchThumbColor,
  switchTrackColor,
  textColor,
  textMutedColor
} from '../../../style/adminTools';
import {dangerTextOnDark} from '../../../style/adminTenantGrid';
import {
  currentPeriodText,
  formatDate,
  formatPlan,
  formatWorkflow,
  isUnlimited,
  lastPeriodText
} from './tenantGridFormat';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

export const hasValue = value =>
  value !== null && value !== undefined && value !== '';

const textStyle = {
  fontFamily: fontFamily.font500,
  fontSize: 13,
  color: textColor
};

const linkStyle = {...textStyle, color: accentColor};

const Dash = () => (
  <Text style={{...textStyle, color: textMutedColor}}>{t('dash')}</Text>
);

const Plain = ({value}) =>
  hasValue(value) ? <Text style={textStyle}>{String(value)}</Text> : <Dash />;

const mutedStyle = {...textStyle, color: textMutedColor};

const CountWithPeriod = ({value, period, countTestID, danger}) =>
  hasValue(value) ? (
    <Text numberOfLines={1}>
      <Text
        testID={countTestID}
        style={danger ? {...textStyle, color: dangerTextOnDark} : textStyle}
      >
        {String(value)}
      </Text>
      {period ? <Text style={mutedStyle}>{` · ${period}`}</Text> : null}
    </Text>
  ) : (
    <Dash />
  );

export const isOverPlanMax = data =>
  hasValue(data.total_shipped) &&
  hasValue(data.max_allowed) &&
  !isUnlimited(data.max_allowed) &&
  Number(data.total_shipped) > Number(data.max_allowed);

const ExternalLink = ({href, label, testID}) => (
  <Text
    testID={testID}
    accessibilityRole="link"
    href={href}
    hrefAttrs={{target: '_blank', rel: 'noopener noreferrer'}}
    style={linkStyle}
  >
    {label}
  </Text>
);

export const parseMoment = moment => {
  const time = moment && moment.date_time ? new Date(moment.date_time) : null;
  return time && !Number.isNaN(time.getTime()) ? time : null;
};

export const latestActivity = lastActivity => {
  if (!lastActivity) return null;
  const candidates = [
    lastActivity.most_recent_login,
    lastActivity.most_recent_scan
  ]
    .map(moment => ({time: parseMoment(moment), user: moment && moment.user}))
    .filter(entry => entry.time);
  if (!candidates.length) return null;
  return candidates.reduce((latest, entry) =>
    entry.time > latest.time ? entry : latest
  );
};

export const formatActivityTime = time =>
  time.toLocaleString(i18n.locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

export const formatClock = timestamp =>
  new Date(timestamp).toLocaleTimeString(i18n.locale, {
    hour: 'numeric',
    minute: '2-digit'
  });

const UsersCell = ({data}) => {
  const regular = data.regular_users_count;
  const adminOnly = data.admin_only_users_count;
  return (
    <View style={{flexDirection: 'row', alignItems: 'center'}}>
      {hasValue(regular) ? (
        <Text style={textStyle}>{String(regular)}</Text>
      ) : (
        <Dash />
      )}
      <Text style={{...textStyle, color: textMutedColor}}>{' | '}</Text>
      {hasValue(adminOnly) ? (
        <Text style={textStyle}>{String(adminOnly)}</Text>
      ) : (
        <Dash />
      )}
    </View>
  );
};

const ActivityCell = ({data}) => {
  const latest = latestActivity(data.last_activity);
  if (!latest)
    return (
      <Text style={{...textStyle, color: textMutedColor}}>
        {t('noActivityYet')}
      </Text>
    );
  return (
    <Text style={textStyle} numberOfLines={1}>
      {formatActivityTime(latest.time)}
      {latest.user ? ` · ${latest.user}` : ''}
    </Text>
  );
};

const TestTenantCell = ({data, onToggle}) => {
  const override = useSelector(
    state => state.adminTenants.testTenantOverrides[data.id]
  );
  const saved = override ? override.value : Boolean(data.test_tenant_toggle);
  const [pending, setPending] = useState(null);
  const inFlight = useRef(false);

  const handleChange = async next => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(next);
    await onToggle(data, next);
    inFlight.current = false;
    setPending(null);
  };
  const value = pending === null ? saved : pending;

  return (
    <Switch
      testID={`testTenantSwitch-${data.id}`}
      accessibilityLabel={`${t('colTestTenant')} ${data.name}`}
      value={value}
      onValueChange={handleChange}
      trackColor={switchTrackColor}
      thumbColor={switchThumbColor}
    />
  );
};

const SORTABLE = new Set([
  'name',
  'plan',
  'progress',
  'transaction_errors',
  'shipped_last',
  'average_shipped_last',
  'total_shipped',
  'average_shipped',
  'max_allowed',
  'users',
  'last_activity',
  'note',
  'last_import_store_type',
  'is_deleted'
]);

const DEFINITIONS = [
  {name: 'name', label: 'colTenant', width: 190, visible: true},
  {name: 'plan', label: 'colPlan', width: 170, visible: true},
  {
    name: 'test_tenant_toggle',
    label: 'colTestTenant',
    width: 140,
    visible: true
  },
  {
    name: 'users',
    label: 'colUsers',
    width: 250,
    sortKey: 'regular_users_count',
    visible: true
  },
  {name: 'last_activity', label: 'colLastActivity', width: 210, visible: true},
  {
    name: 'total_shipped',
    label: 'colShippedThisMonth',
    width: 230,
    visible: true
  },
  {
    name: 'shipped_last',
    label: 'colShippedLastMonth',
    width: 290,
    visible: true
  },
  {name: 'note', label: 'colNotes', width: 240, visible: true},
  {
    name: 'last_charge_in_stripe',
    label: 'colLastCharge',
    width: 190,
    visible: true
  },
  {name: 'created_at', label: 'colCreated', width: 140, visible: true},
  {name: 'scan_pack_workflow', label: 'colWorkflow', width: 190, visible: true},
  {
    name: 'last_import_store_type',
    label: 'colLastImportStoreType',
    width: 200,
    visible: true
  },
  {name: 'url', label: 'colUrl', width: 250, visible: true},
  {name: 'start_day', label: 'colStartDay', width: 140, visible: true},
  {name: 'stripe_url', label: 'colStripe', width: 100, visible: false},
  {
    name: 'average_shipped_last',
    label: 'colAvgShippedLastMonth',
    width: 210,
    visible: false
  },
  {
    name: 'average_shipped',
    label: 'colAvgShippedThisMonth',
    width: 210,
    visible: false
  },
  {name: 'max_allowed', label: 'colPlanMax', width: 120, visible: false}
];

export const sortParam = name => {
  const definition = DEFINITIONS.find(candidate => candidate.name === name);
  return (definition && definition.sortKey) || name;
};

export const COLUMN_NAMES = DEFINITIONS.map(definition => definition.name);

export const DEFAULT_HIDDEN_COLUMNS = Object.fromEntries(
  DEFINITIONS.map(definition => [definition.name, !definition.visible])
);

export const hiddenFromVisible = visibleNames => {
  const known = visibleNames.filter(name => COLUMN_NAMES.includes(name));
  if (!known.length) return DEFAULT_HIDDEN_COLUMNS;
  return Object.fromEntries(
    COLUMN_NAMES.map(name => [name, !known.includes(name)])
  );
};

export const visibleFromHidden = hidden =>
  COLUMN_NAMES.filter(name => !hidden[name]);

export const columnLabels = () =>
  DEFINITIONS.map(definition => ({
    name: definition.name,
    label: t(definition.label)
  }));

// A react-native-web Text would reset the grid header's dark-theme colour and font (productGrid.css).
const headerLabelStyle = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

export const buildTenantColumns = ({
  hidden,
  onOpenTenant,
  onToggleTestTenant
}) => {
  const renderers = {
    name: ({data}) => (
      <TouchableOpacity
        testID={`tenantLink-${data.id}`}
        accessibilityRole="link"
        onPress={() => onOpenTenant(data)}
      >
        <Text style={{...linkStyle, fontFamily: fontFamily.font600}}>
          {data.name}
        </Text>
      </TouchableOpacity>
    ),
    plan: ({data}) => <Plain value={formatPlan(data)} />,
    start_day: ({data}) => (
      <Plain value={formatDate(data.start_date) || data.start_day} />
    ),
    shipped_last: ({data}) => (
      <CountWithPeriod
        value={data.shipped_last}
        period={lastPeriodText(data)}
      />
    ),
    average_shipped_last: ({data}) => (
      <Plain value={data.average_shipped_last} />
    ),
    total_shipped: ({data}) => (
      <CountWithPeriod
        countTestID={`totalShipped-${data.id}`}
        value={data.total_shipped}
        period={currentPeriodText(data)}
        danger={isOverPlanMax(data)}
      />
    ),
    average_shipped: ({data}) => <Plain value={data.average_shipped} />,
    max_allowed: ({data}) => (
      <Plain
        value={
          isUnlimited(data.max_allowed) ? t('unlimited') : data.max_allowed
        }
      />
    ),
    created_at: ({data}) => <Plain value={formatDate(data.created_at)} />,
    scan_pack_workflow: ({data}) => (
      <Plain value={formatWorkflow(data.scan_pack_workflow)} />
    ),
    users: UsersCell,
    last_activity: ActivityCell,
    note: ({data}) => <Plain value={data.note} />,
    url: ({data}) =>
      hasValue(data.url) ? (
        <ExternalLink
          testID={`tenantUrl-${data.id}`}
          href={`http://${data.url}`}
          label={data.url}
        />
      ) : (
        <Dash />
      ),
    stripe_url: ({data}) =>
      hasValue(data.stripe_url) ? (
        <ExternalLink
          testID={`tenantStripe-${data.id}`}
          href={`https://dashboard.stripe.com/customers/${data.stripe_url}`}
          label={t('open')}
        />
      ) : (
        <Dash />
      ),
    test_tenant_toggle: ({data}) => (
      <TestTenantCell key={data.id} data={data} onToggle={onToggleTestTenant} />
    ),
    last_charge_in_stripe: ({data}) =>
      hasValue(data.last_charge_in_stripe) ? (
        <Text style={textStyle}>{data.last_charge_in_stripe}</Text>
      ) : (
        <Text style={{...textStyle, color: textMutedColor}}>
          {t('notChargedYet')}
        </Text>
      ),
    last_import_store_type: ({data}) => (
      <Plain value={data.last_import_store_type} />
    )
  };

  return DEFINITIONS.map(({name, label, width}) => ({
    name,
    header: (
      <span data-testid={`tenantColumn-${name}`} style={headerLabelStyle}>
        {t(label)}
      </span>
    ),
    defaultWidth: width,
    minWidth: 80,
    sortable: SORTABLE.has(name),
    visible: !hidden[name],
    render: props => (
      <View testID={`tenantCell-${name}-${props.data.id}`}>
        {renderers[name](props)}
      </View>
    )
  }));
};
